/**
 * QUEM ESTÁ USANDO ESTE APARELHO — a conversa com o servidor sobre login.
 *
 * O crachá mora num cookie que o JavaScript não alcança; esta camada nunca vê
 * senha nem crachá. Ela guarda só uma cópia de "quem sou eu" (nome, papel,
 * empresa) para o app abrir SEM SINAL: no curral, sem internet, o app não
 * pode pedir login. Só uma recusa explícita do servidor (401) devolve à tela
 * de entrar — falta de sinal nunca.
 */
import { useSyncExternalStore } from 'react'
import { abrirDoDono } from './armazem'
import { ferramentaLigada, type Ferramentas, type NomeDaFerramenta, type Vinculo } from '../dominio/ferramentas'
import { pode, type Acao, type Contexto, type Papel } from '../dominio/permissoes'

export type { Papel }
/** 3C: `vinculo` (contratado/comissionado) só vem depois da migração 011; ausente = não definido. */
export interface Pessoa { id: string; nome: string; email: string; papel: Papel; sigla: string; plataforma: boolean; vinculo?: Vinculo | null }
/** 3C: `ferramentas` ausente (antes da 011, ou «eu» guardado por versão antiga) = tudo ligado. */
export interface Conta {
  id: string; nome_fantasia: string; razao_social: string; doc: string; telefone: string; cidade: string; plano: string
  ferramentas?: Ferramentas | null
}
/**
 * `vinculos`: só o gestor recebe — o vínculo de cada comprador, para a despesa
 * que ele lança no extrato de qualquer um nascer com o padrão certo, sem sinal.
 */
export interface Eu { pessoa: Pessoa; conta: Conta; vinculos?: Record<string, Vinculo | null> }

export type EstadoDaSessao =
  | { tipo: 'carregando' }
  | { tipo: 'fora' }
  | { tipo: 'dentro'; eu: Eu; semSinal: boolean }

const CHAVE = 'pesagem.eu'

function lerGuardado(): Eu | null {
  try {
    const t = localStorage.getItem(CHAVE)
    const eu = t ? (JSON.parse(t) as Eu) : null
    return eu?.pessoa?.id && eu?.conta?.id ? eu : null
  } catch { return null }
}
function guardar(eu: Eu | null): void {
  try { if (eu) localStorage.setItem(CHAVE, JSON.stringify(eu)); else localStorage.removeItem(CHAVE) } catch { /* sem disco */ }
}

let estado: EstadoDaSessao = { tipo: 'carregando' }
const ouvintes = new Set<() => void>()
function mudar(e: EstadoDaSessao): void { estado = e; ouvintes.forEach((f) => f()) }

/** R2D · A sessão fora de uma tela (ex.: a venda do estoque que sai sozinha ao encerrar). */
export function lerSessao(): EstadoDaSessao {
  return estado
}

export function usarSessao(): EstadoDaSessao {
  return useSyncExternalStore((f) => { ouvintes.add(f); return () => ouvintes.delete(f) }, () => estado)
}

export class Recusa extends Error {
  constructor(mensagem: string, public status: number, public dados?: Record<string, unknown>) { super(mensagem) }
}

/** Chamada ao servidor. Erro de rede vira `SemSinal`; resposta de erro vira `Recusa` com a frase do servidor. */
export class SemSinal extends Error {}
export async function api<T = Record<string, unknown>>(caminho: string, corpo?: unknown): Promise<T> {
  let r: Response
  try {
    r = await fetch(`/api/${caminho}`, {
      method: corpo === undefined ? 'GET' : 'POST',
      headers: corpo === undefined ? {} : { 'content-type': 'application/json' },
      body: corpo === undefined ? null : JSON.stringify(corpo),
      credentials: 'same-origin',
    })
  } catch {
    throw new SemSinal('Sem conexão com a internet.')
  }
  // resposta que não é do servidor do Pesagem (portal de wi-fi, página no lugar
  // da API) conta como falta de sinal — nunca como "deu certo"
  if (!(r.headers.get('content-type') ?? '').includes('application/json')) throw new SemSinal('Servidor fora do ar.')
  let dados: Record<string, unknown> = {}
  try { dados = await r.json() } catch { throw new SemSinal('Resposta incompleta do servidor.') }
  if (r.status === 401 && caminho !== 'entrar') {
    guardar(null)
    mudar({ tipo: 'fora' })
  }
  if (!r.ok) throw new Recusa(String(dados['erro'] ?? 'Não deu para concluir. Tente de novo.'), r.status, dados)
  return dados as T
}

/** Os dados guardados no aparelho são de quem entrou: abre os dela. */
function abrir(eu: Eu): void {
  abrirDoDono({ id: eu.pessoa.id, nome: eu.pessoa.nome, sigla: eu.pessoa.sigla, contaId: eu.conta.id, papel: eu.pessoa.papel })
}

/** Ao abrir o app: pergunta ao servidor; sem sinal, segue com o que está guardado. */
export async function conferirSessao(): Promise<void> {
  // saiu sem sinal da última vez: termina a saída e fica na tela de entrar
  if (saidaPendente()) {
    await completarSaida()
    mudar({ tipo: 'fora' })
    return
  }
  try {
    const eu = await api<Eu>('eu')
    if (!eu?.pessoa?.id || !eu?.conta?.id) throw new SemSinal('Resposta incompleta do servidor.')
    guardar(eu)
    abrir(eu)
    mudar({ tipo: 'dentro', eu, semSinal: false })
  } catch (e) {
    if (e instanceof SemSinal) {
      const guardado = lerGuardado()
      if (guardado) abrir(guardado)
      mudar(guardado ? { tipo: 'dentro', eu: guardado, semSinal: true } : { tipo: 'fora' })
    } else if (estado.tipo !== 'fora') {
      mudar({ tipo: 'fora' })
    }
  }
}

export async function entrar(email: string, senha: string): Promise<void> {
  // termina primeiro a saída antiga (se houver), para ela não derrubar o login novo
  if (saidaPendente()) await completarSaida()
  await api('entrar', { email, senha })
  entrouDeNovo()
  await conferirSessao()
}

/** Login novo (senha ou link): a saída pendente de antes não vale mais. */
export function entrouDeNovo(): void {
  try { localStorage.removeItem(SAIDA_PENDENTE) } catch { /* sem disco */ }
}

/**
 * Sair. O crachá mora num cookie que só o servidor apaga — então, sem sinal,
 * fica anotado «saída pendente» no aparelho: o app NÃO entra sozinho de novo
 * quando o sinal voltar, e completa a saída antes de qualquer outra coisa
 * (achado da revisão de segurança: celular dividido no curral).
 */
const SAIDA_PENDENTE = 'pesagem.saida-pendente'

/**
 * R1A (revisão de 06/10/2026): ao sair, os dados sigilosos da pessoa SAEM do
 * aparelho — comissões, quitações, preço de venda, extrato, catira, notas e
 * as fotos que já estão no servidor. Antes eles ficavam no celular dividido
 * depois do «Sair». Com pendência, primeiro tenta «Sincronizar» e, se ainda
 * sobrar alguma coisa, pergunta. O que não chegou ao banco FICA (nada se
 * perde): as fichas presas ficam para o gestor poder trazê-las para ele.
 * Devolve `false` se a pessoa desistiu de sair.
 */
export async function sair(): Promise<boolean> {
  const fila = await import('./fila')
  if (fila.contarPendentes() > 0) {
    let semSinal = false
    try { semSinal = (await fila.sincronizar()).semSinal } catch { semSinal = true }
    const n = fila.contarPendentes()
    if (n > 0 && !window.confirm(
      `Ainda ${n === 1 ? 'há 1 registro que não chegou' : `há ${n} registros que não chegaram`} ao banco`
      + `${semSinal ? ' (sem sinal agora)' : ''}.\n\nSincronize antes de sair, se puder. Se sair assim mesmo, `
      + `${n === 1 ? 'ele fica guardado' : 'eles ficam guardados'} só neste aparelho até você entrar de novo `
      + '(as fichas, o gestor também pode trazer para ele).\n\nSair assim mesmo?')) return false
  }
  try { await fila.enxugarAoSair() } catch { /* sem disco ou sem IndexedDB: sai assim mesmo */ }
  try { localStorage.setItem(SAIDA_PENDENTE, '1') } catch { /* sem disco */ }
  guardar(null)
  mudar({ tipo: 'fora' })
  await completarSaida()
  return true
}

async function completarSaida(): Promise<boolean> {
  try {
    await api('sair', {})
    try { localStorage.removeItem(SAIDA_PENDENTE) } catch { /* sem disco */ }
    return true
  } catch {
    return false
  }
}

function saidaPendente(): boolean {
  try { return localStorage.getItem(SAIDA_PENDENTE) === '1' } catch { return false }
}

/**
 * Atualiza a cópia guardada depois de mudar os dados da empresa. JUNTA com o
 * que já está guardado: a rota dos dados da empresa não devolve as
 * ferramentas, e sem isso salvar o nome fantasia «religaria» tudo na tela.
 */
export function atualizarConta(conta: Partial<Conta>): void {
  if (estado.tipo !== 'dentro') return
  const eu = { ...estado.eu, conta: { ...estado.eu.conta, ...conta } }
  guardar(eu)
  mudar({ ...estado, eu })
}

/** 3C: o gestor mudou o vínculo de alguém na Equipe — o padrão da despesa já vale neste aparelho. */
export function atualizarVinculo(pessoaId: string, vinculo: Vinculo | null): void {
  if (estado.tipo !== 'dentro') return
  const e = estado.eu
  const eu: Eu = {
    ...e,
    pessoa: e.pessoa.id === pessoaId ? { ...e.pessoa, vinculo } : e.pessoa,
    vinculos: e.vinculos ? { ...e.vinculos, [pessoaId]: vinculo } : e.vinculos,
  }
  guardar(eu)
  mudar({ ...estado, eu })
}

/**
 * 3C · A ferramenta está ligada nesta empresa? Lê a cópia guardada do «eu»,
 * então vale sem sinal. Desligada, a tela só esconde — os dados continuam
 * sincronizando normalmente (nada fica preso na fila).
 */
export function usarFerramenta(nome: NomeDaFerramenta): boolean {
  const s = usarSessao()
  return s.tipo === 'dentro' ? ferramentaLigada(s.eu.conta, nome) : true
}

/**
 * 3E · O papel de quem está usando, para a tela perguntar a `pode(...)`.
 * Fora da sessão (não deveria acontecer dentro do app) vale a Consulta: na
 * dúvida, a tela não oferece gravar.
 */
export function usarPapel(): Papel {
  const s = usarSessao()
  return s.tipo === 'dentro' ? s.eu.pessoa.papel : 'consulta'
}

/** 3E · Atalho: «este papel pode isto?», lido da sessão (vale sem sinal). */
export function usarPode(): (acao: Acao, contexto?: Contexto) => boolean {
  const papel = usarPapel()
  return (acao, contexto) => pode(papel, acao, contexto)
}
